/**
 * Disciplines Tab - DataGrid Version with Full CRUD
 *
 * Uses Material-UI DataGrid for enhanced filtering, sorting, and pagination
 * Manages disciplines for a studio (e.g., Dance, Acting, Voice, Instrumental)
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetDisciplinesQuery,
  useCreateDisciplineMutation,
  useUpdateDisciplineMutation,
  useDeleteDisciplineMutation,
} from '../../../api/schedulingApi';
import type { Discipline } from '../../../types/scheduling';

interface DisciplinesTabProps {
  studioId: string;
}

interface DisciplineFormData {
  title: string;
  description?: string;
}

interface DisciplineFormErrors {
  title?: string;
  description?: string;
}

const DisciplinesTabDataGrid: React.FC<DisciplinesTabProps> = ({
  studioId,
}) => {
  const { data: disciplines, isLoading } = useGetDisciplinesQuery(studioId);
  const [createDiscipline, { isLoading: isCreating }] =
    useCreateDisciplineMutation();
  const [updateDiscipline, { isLoading: isUpdating }] =
    useUpdateDisciplineMutation();
  const [deleteDiscipline, { isLoading: isDeleting }] =
    useDeleteDisciplineMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDiscipline, setEditingDiscipline] = useState<Discipline | null>(
    null
  );
  const [deleteDialogDiscipline, setDeleteDialogDiscipline] =
    useState<Discipline | null>(null);

  // Form state
  const [formData, setFormData] = useState<DisciplineFormData>({
    title: '',
    description: '',
  });

  const [formErrors, setFormErrors] = useState<DisciplineFormErrors>({});

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: DisciplineFormErrors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = (discipline: Discipline) => {
    setEditingDiscipline(discipline);
    setFormData({
      title: discipline.title,
      description: discipline.description || '',
    });
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setEditingDiscipline(null);
    resetForm();
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      await createDiscipline({
        studioId,
        data: {
          title: formData.title,
          description: formData.description || undefined,
        },
      }).unwrap();
      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to create discipline:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingDiscipline || !validateForm()) return;

    try {
      await updateDiscipline({
        id: editingDiscipline.id,
        studioId,
        data: {
          title: formData.title,
          description: formData.description || undefined,
        },
      }).unwrap();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Failed to update discipline:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialogDiscipline) return;

    try {
      await deleteDiscipline({
        id: deleteDialogDiscipline.id,
        studioId,
      }).unwrap();
      setDeleteDialogDiscipline(null);
    } catch (error) {
      console.error('Failed to delete discipline:', error);
    }
  };

  // DataGrid columns configuration
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 200,
        renderCell: params => (
          <Typography variant="body2" color="text.secondary">
            {params.value || '—'}
          </Typography>
        ),
      },
      {
        field: 'style_count',
        headerName: 'Styles Count',
        width: 130,
        renderCell: params => (
          <Chip
            label={`${params.value} styles`}
            size="small"
            color={params.value > 0 ? 'primary' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params: GridRowParams<Discipline>) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleOpenEditDialog(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setDeleteDialogDiscipline(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6">Disciplines</Typography>
          <Typography variant="body2" color="text.secondary">
            High-level categories for organizing styles (e.g., Dance, Acting,
            Voice, Instrumental)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Discipline
        </Button>
      </Stack>

      {!disciplines || disciplines.length === 0 ? (
        <Alert severity="info">
          No disciplines added yet. Click &quot;Add Discipline&quot; to create
          your first discipline category.
        </Alert>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={disciplines}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      )}

      {/* Add Discipline Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Discipline</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!formErrors.title}
              helperText={formErrors.title || 'e.g., Dance, Acting, Voice'}
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional description of this discipline"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreating}
          >
            Add Discipline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Discipline Dialog */}
      <Dialog
        open={!!editingDiscipline}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Discipline</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!formErrors.title}
              helperText={formErrors.title || 'e.g., Dance, Acting, Voice'}
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional description of this discipline"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogDiscipline}
        onClose={() => setDeleteDialogDiscipline(null)}
      >
        <DialogTitle>Delete Discipline?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;
            {deleteDialogDiscipline?.title}&quot;?
          </Typography>
          {deleteDialogDiscipline && deleteDialogDiscipline.style_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This discipline has {deleteDialogDiscipline.style_count}{' '}
              associated styles. Deleting it may affect those styles.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogDiscipline(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisciplinesTabDataGrid;
